import { Injectable, inject } from '@angular/core';
import { UtilsService } from '../utils/utils.service';
import device from 'current-device';
import { WebviewService } from '../platform/webview.service';
import { SettingsService, Unit } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import concatAudioBuffers from 'concat-audio-buffers';
import { Turn } from '../../models/route';
import { UnitSettings } from '../../models/unit-settings';
import { HttpService } from '../core/http.service';

@Injectable({
  providedIn: 'root'
})

/**
 * playing voice instruction audio files
*/

export class AudioService {
  private audio!: HTMLAudioElement;
  private audioContext!: AudioContext;
  private audioSourceUrl!: string;
  private gainNode!: GainNode;
  private bufferNode: AudioBufferSourceNode | null = null;
  private playPromise: Promise<void> | undefined;
  private speechActive: boolean = false;

  private readonly pagehideHandler = () => { this.audioContext?.close(); };

  private readonly utilsService = inject(UtilsService);
  private readonly httpService = inject(HttpService);
  public readonly webviewService = inject(WebviewService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);

  constructor() {
    this.initAudioContext();
    window.addEventListener('pagehide', this.pagehideHandler);
  }

  private initAudioContext(): void {
    this.audio = new Audio();
    let AudioContext = globalThis.AudioContext || (globalThis as any).webkitAudioContext;
    this.audioContext = new AudioContext();
    this.gainNode = this.audioContext.createGain();
  }

  private isMuted(): boolean {
    return this.settingsService.getVoiceGuidence().Mute || this.speechActive;
  }

  public mute(mute: boolean): void {
    this.audio.muted = mute;
    this.gainNode.gain.value = mute ? 0 : 1;
  }

  public StartNavigation(): void {
    if (this.isMuted()) {
      return;
    }

    if (!this.webviewService.IsGPSTWebview() && !globalThis.AudioContext) {
      this.Beep();
    }
  }

  public Beep(): void {
    this.mergeAndPlayAudio(["assets/audio/beep.mp3"]);
  }

  public playVoiceCommandApprovedSignal(): void {
    this.mergeAndPlayAudio(["assets/audio/voice_recognition/voice_command_approved.mp3"]);
  }

  public playVoiceCommandDiscardedSignal(): void {
    this.mergeAndPlayAudio(["assets/audio/voice_recognition/voice_command_discarded.mp3"]);
  }

  public readCommand(dist: number, NextTurn: Turn, unit: UnitSettings): void {
    let audioPlayList: string[] = [];

    dist = this.RoundDistance(dist, unit);
    audioPlayList.push("assets/audio/beep.mp3");

    if (dist != 0) {
      // in x distance
      audioPlayList.push(this.getAudioSource() + "Commands/In.mp3");

      audioPlayList = audioPlayList.concat(this.getSoundFiles(dist, unit, "meter") ?? []);
      if (dist < 1000 && unit.Distance == Unit.Metric) {
        audioPlayList.push(this.getAudioSource() + "meters.mp3");
      }

      if (dist < 1760 && unit.Distance == Unit.Imperial) {
        audioPlayList.push(this.getAudioSource() + "yards.mp3");
      }
    }
    if (dist >= 1000 && dist < 2000 && unit.Distance == Unit.Metric) {
      audioPlayList.push(this.getAudioSource() + "kilometer.mp3");
    }
    if (dist >= 2000 && unit.Distance == Unit.Metric) {
      audioPlayList.push(this.getAudioSource() + "kilometers.mp3");
    }
    if (dist >= 1760 && unit.Distance == Unit.Imperial) {
      audioPlayList.push(this.getAudioSource() + "miles.mp3");
    }

    // turn instruction
    if (NextTurn.Id == "ENTER_ROUNDABOUT" || NextTurn.Id == "EXIT_ROUNDABOUT") {
      audioPlayList.push(
        this.getAudioSource() + "Commands/AtRoundabout.mp3",
        this.getAudioSource() + "Commands/Take" + NextTurn.RoundaboutExitNumber + "Exit.mp3"
      );
    }
    else {
      audioPlayList.push(this.getAudioSource() + "Commands/" + this.utilsService.turnList[NextTurn.TypeIndex].sound);
    }
    this.mergeAndPlayAudio(audioPlayList);
  }

  public readFollowTheRoute(dist: number, unit: UnitSettings): void {
    let audioPlayList: string[] = [];

    dist = this.RoundDistance(dist, unit);
    audioPlayList.push("assets/audio/beep.mp3", this.getAudioSource() + "Commands/FollowRoute.mp3");
    audioPlayList = audioPlayList.concat(this.getSoundFiles(dist, unit, "meter") ?? []);
    if (dist < 1000 && unit.Distance == Unit.Metric) {
      audioPlayList.push(this.getAudioSource() + "meters.mp3");
    }

    if (dist >= 1000 && dist < 2000 && unit.Distance == Unit.Metric) {
      audioPlayList.push(this.getAudioSource() + "kilometer.mp3");
    }
    if (dist >= 2000 && unit.Distance == Unit.Metric) {
      audioPlayList.push(this.getAudioSource() + "kilometers.mp3");
    }
    if (dist < 1760 && unit.Distance == Unit.Imperial) {
      audioPlayList.push(this.getAudioSource() + "yards.mp3");
    }
    if (dist >= 1760 && unit.Distance == Unit.Imperial) {
      audioPlayList.push(this.getAudioSource() + "miles.mp3");
    }

    this.mergeAndPlayAudio(audioPlayList);
  }

  public getSoundFiles(number: number, unit: UnitSettings, unitType, standalone: boolean = true): string[] | null {
    switch (this.languageService.getLanguage()) {
      /** ENGLISH, SPANISH */
      case "en":
      case "es":
        return this.getSoundFilesEnEs(number, unit, unitType);
      /** ITALIAN */
      case "it":
        return this.getSoundFilesIt(number, unit, unitType);
      /** GERMAN */
      case "de":
        return this.getSoundFilesDe(number, unit, unitType, standalone);
      /** FRENCH */
      case "fr":
        return this.getSoundFilesFr(number, unit, unitType);
      default:
        return [];
    }
  }

  private getSoundFilesEnEs(number: number, unit: UnitSettings, unitType): string[] {
    // read dot
    if (this.hasDecimal(number)) {
      return this.getDecimalSoundFiles(number, unit);
    }
    // 1-19, 100 200 ..., 10 20 ...
    if ((number > 0 && number <= 19) || this.isRoundHundred(number) || this.isRoundTen(number)) {
      return [this.getNumberSoundFile(number)];
    }
    // Change to km / miles
    const converted = this.getUnitConversionSoundFiles(number, unit, unitType);
    if (converted) {
      return converted;
    }
    // 1000X, 100X
    if (number >= 100) {
      return this.getHundredsOrThousandsSoundFiles(number, unit, unitType);
    }
    // 10X
    if (number >= 20) {
      return this.getCompoundSoundFiles(number, 10, unit, unitType);
    }

    return [];
  }

  private getSoundFilesIt(number: number, unit: UnitSettings, unitType): string[] | null {
    // read dot
    if (this.hasDecimal(number)) {
      return this.getDecimalSoundFiles(number, unit);
    }
    if (number >= 0 && number <= 19) {
      return [this.getNumberSoundFile(number)];
    }
    // Change to km / miles
    const converted = this.getUnitConversionSoundFiles(number, unit, unitType);
    if (converted) {
      return converted;
    }
    if (number > 5999) {
      return null;
    }
    if (this.isRoundThousand(number) || this.isRoundHundred(number) || this.isRoundTen(number)) {
      return [this.getNumberSoundFile(number)];
    }
    if (number >= 100) {
      return this.getHundredsOrThousandsSoundFiles(number, unit, unitType);
    }
    if (number >= 20) {
      return this.getItalianTensSoundFiles(number, unit, unitType);
    }

    return [];
  }

  private getItalianTensSoundFiles(number: number, unit: UnitSettings, unitType): string[] {
    // 21, 28, 31, 38 ... are recorded as whole words (ventuno, ventotto)
    if (number % 10 == 1 || number % 10 == 8) {
      return [this.getNumberSoundFile(number)];
    }

    return this.getCompoundSoundFiles(number, 10, unit, unitType);
  }

  private getSoundFilesDe(number: number, unit: UnitSettings, unitType, standalone: boolean): string[] | null {
    // read dot
    if (this.hasDecimal(number)) {
      return this.getDecimalSoundFiles(number, unit);
    }
    // dative "in einem Kilometer" / "in einer Meile" - only when 1 is the entire spoken value,
    // never as the tail of a compound (601 must end in "eins", not "einem")
    if (standalone && number == 1) {
      return [this.getGermanDativeOneSoundFile(unit, unitType)];
    }
    if (number >= 0 && number <= 19) {
      return [this.getNumberSoundFile(number)];
    }
    // Change to km / miles
    const converted = this.getUnitConversionSoundFiles(number, unit, unitType);
    if (converted) {
      return converted;
    }
    if (number > 5999) {
      return null;
    }
    if (this.isRoundThousand(number) || this.isRoundHundred(number) || this.isRoundTen(number)) {
      return [this.getNumberSoundFile(number)];
    }
    if (number >= 100) {
      return this.getHundredsOrThousandsSoundFiles(number, unit, unitType);
    }
    if (number >= 20) {
      return this.getGermanTensSoundFiles(number);
    }

    return [];
  }

  private getGermanDativeOneSoundFile(unit: UnitSettings, unitType): string {
    if (unit.Distance == Unit.Imperial && unitType == "km") {
      return this.getAudioSource() + "Numbers/einer.mp3";
    }

    return this.getAudioSource() + "Numbers/einem.mp3";
  }

  private getGermanTensSoundFiles(number: number): string[] {
    const audioPlayList: string[] = [];

    // German says the ones first: "vierundfünfzig"
    if (number % 10) {
      audioPlayList.push(this.getAudioSource() + "Numbers/X" + Math.trunc(number % 10) + ".mp3");
    }

    audioPlayList.push(this.getNumberSoundFile(Math.trunc(number / 10) * 10));
    return audioPlayList;
  }

  private getSoundFilesFr(number: number, unit: UnitSettings, unitType): string[] | null {
    // read dot
    if (this.hasDecimal(number)) {
      return this.getDecimalSoundFiles(number, unit);
    }
    if (number == 71 || number == 81 || (number >= 0 && number <= 19)) {
      return [this.getNumberSoundFile(number)];
    }
    // Change to km / miles
    const converted = this.getUnitConversionSoundFiles(number, unit, unitType);
    if (converted) {
      return converted;
    }
    if (number > 5999) {
      return null;
    }
    if (this.isRoundThousand(number) || this.isRoundHundred(number) || (this.isRoundTen(number) && number != 70 && number != 90)) {
      return [this.getNumberSoundFile(number)];
    }
    if (number >= 100) {
      return this.getHundredsOrThousandsSoundFiles(number, unit, unitType);
    }
    if (number >= 20) {
      return this.getFrenchTensSoundFiles(number, unit, unitType);
    }

    return [];
  }

  private getFrenchTensSoundFiles(number: number, unit: UnitSettings, unitType): string[] {
    if (number < 70 && number % 10 == 1) {
      return [
        this.getAudioSource() + "Numbers/" + Math.trunc(number / 10) * 10 + "X.mp3",
        this.getAudioSource() + "Numbers/X1.mp3"
      ];
    }
    if (number < 70) {
      return this.getCompoundSoundFiles(number, 10, unit, unitType);
    }
    // 70-79 is "soixante" + 10-19, 80-99 is "quatre-vingt" + 0-19
    if (number < 80) {
      return [this.getAudioSource() + "Numbers/60.mp3", ...this.getRemainderSoundFiles(number - 60, unit, unitType)];
    }
    if (number < 100) {
      return [this.getAudioSource() + "Numbers/80.mp3", ...this.getRemainderSoundFiles(number - 80, unit, unitType)];
    }

    return [];
  }

  private getNumberSoundFile(number: number): string {
    return this.getAudioSource() + "Numbers/" + number + ".mp3";
  }

  private hasDecimal(number: number): boolean {
    return number.toString().includes(".");
  }

  private isRoundThousand(number: number): boolean {
    return number % 1000 == 0 && Math.trunc(number / 10000) == 0;
  }

  private isRoundHundred(number: number): boolean {
    return number % 100 == 0 && Math.trunc(number / 1000) == 0;
  }

  private isRoundTen(number: number): boolean {
    return number % 10 == 0 && Math.trunc(number / 100) == 0;
  }

  /** "3 point 5": the whole part, "point", then the fraction digits read as a number. */
  private getDecimalSoundFiles(number: number, unit: UnitSettings): string[] {
    return [
      this.getNumberSoundFile(Math.trunc(number)),
      this.getAudioSource() + "point.mp3",
      ...(this.getSoundFiles(Number.parseInt(number.toString().split(".")[1]), unit, "km", false) ?? [])
    ];
  }

  /** Metre / yard counts that are spoken in kilometres / miles; null when no conversion applies. */
  private getUnitConversionSoundFiles(number: number, unit: UnitSettings, unitType): string[] | null {
    if (number >= 1000 && unit.Distance == Unit.Metric && unitType == "meter") {
      return this.metersToKm(number, unit) ?? [];
    }
    if (number >= 1760 && unit.Distance == Unit.Imperial && unitType == "meter") {
      return this.yardsToMiles(number, unit) ?? [];
    }

    return null;
  }

  /** 1000X or 100X followed by the remainder; only called for numbers >= 100. */
  private getHundredsOrThousandsSoundFiles(number: number, unit: UnitSettings, unitType): string[] {
    const divisor = number >= 1000 ? 1000 : 100;
    return this.getCompoundSoundFiles(number, divisor, unit, unitType);
  }

  /** The "<n>X" prefix recording for the leading part, followed by the remainder. */
  private getCompoundSoundFiles(number: number, divisor: number, unit: UnitSettings, unitType): string[] {
    return [
      this.getAudioSource() + "Numbers/" + Math.trunc(number / divisor) * divisor + "X.mp3",
      ...this.getRemainderSoundFiles(number % divisor, unit, unitType)
    ];
  }

  /**
   * Tail of a compound number. Nothing is spoken for a zero remainder, so 2000 stays
   * "two thousand" instead of "two thousand zero".
   */
  private getRemainderSoundFiles(remainder: number, unit: UnitSettings, unitType): string[] {
    if (remainder <= 0) {
      return [];
    }

    return this.getSoundFiles(remainder, unit, unitType, false) ?? [];
  }

  /**
   * The caller appends "kilometer(s)", so a metre count has to be spoken as its kilometre
   * value: 3500 m is "3 point 5", not "3500".
   */
  private metersToKm(meters: number, unit: UnitSettings): string[] | null {
    const km = meters >= 10000 ? Math.round(meters / 1000) : Math.round(meters / 100) / 10;
    return this.getSoundFiles(km, unit, "km");
  }

  /** Same switch for imperial: the caller appends "miles", so yards are spoken as miles. */
  private yardsToMiles(yards: number, unit: UnitSettings): string[] | null {
    const miles = yards >= 17600 ? Math.round(yards / 1760) : Math.round(yards / 176) / 10;
    return this.getSoundFiles(miles, unit, "km");
  }

  // merging mp3 files
  public mergeAndPlayAudio(audioPlayList: string[], voiceCommand?: boolean): void {
    if (this.isMuted()) {
      return;
    }

    this.httpService.getAudioPlayList(audioPlayList).subscribe((resp: ArrayBuffer[]) => {
      if (this.webviewService.IsGPSTWebview() && device.ios()) {
        this.webviewService.PlayAudio(resp);
      }
      else if (this.webviewService.IsGPSTWebview() && device.android()) {
        const mergedAudio = this.MergeArrayBuffersFilesToUint8Arr(resp);
        this.webviewService.PlayAudioAndroid(mergedAudio);
      }
      else if (globalThis.AudioContext) {
        this.mergeAndPlayBuffers(resp);
      }
      else {
        this.playMergedAsAudio(resp);
      }
    });
  }

  // convert arrayBuffers to audioBuffers and merge them
  private mergeAndPlayBuffers(arrayBuffers: ArrayBuffer[]): void {
    let decodePromises: Promise<AudioBuffer>[] = [];

    for (const arrayBuffer of arrayBuffers) {
      decodePromises.push(this.audioContext.decodeAudioData(arrayBuffer));
    }

    Promise.all(decodePromises).then((audioBuffers) => {

      concatAudioBuffers(audioBuffers, 1, (error, combinedBuffer) => {
        if (!error) {
          this.playAudioContext(combinedBuffer);
        }
      });
    })
  }

  // play merged audiobuffers
  private playAudioContext(mergedAudio: AudioBuffer): void {
    if (this.bufferNode) {
      this.bufferNode.disconnect();
    }
    this.bufferNode = this.audioContext.createBufferSource();
    this.bufferNode.buffer = mergedAudio;
    this.bufferNode.connect(this.audioContext.destination);
    this.bufferNode.start();
  }

  // play single audio file
  private playMergedAsAudio(arraybuffer: ArrayBuffer[]): void {
    if (this.playPromise == undefined) {
      this.audioSourceUrl = globalThis.URL.createObjectURL(new Blob(arraybuffer, { type: "audio/mp3" }));

      this.audio.src = this.audioSourceUrl;
      this.audio.load();
      this.playPromise = this.audio.play();
      this.playPromise.then(() => {
        globalThis.URL.revokeObjectURL(this.audioSourceUrl);
      });
    }
    else {
      this.playPromise.then(() => {
        const url = globalThis.URL.createObjectURL(new Blob(arraybuffer, { type: "audio/mp3" }));
        this.audio.src = url;
        this.audio.load();
        this.playPromise = this.audio.play();
        this.playPromise.then(() => {
          globalThis.URL.revokeObjectURL(this.audioSourceUrl);
        });
      })
    }
  }

  public playKeepAlive(): void {
    this.audio.src = "assets/audio/keep-alive.mp3";
    this.audio.load();
    this.audio.play();
  }

  /** return source of the audio with the selected language folder*/
  public getAudioSource(): string {

    let voiceLib: string;

    switch (this.languageService.getLanguage()) {
      case "de":
        voiceLib = "de-DE_Marlene";
        break;
      case "es":
        voiceLib = "es-ES_Conchita";
        break;
      case "fr":
        voiceLib = "fr-FR_Celine";
        break;
      case "it":
        voiceLib = "it-IT_Carla";
        break;
      default: // includes "en"
        voiceLib = "en-US_Kendra";
        break;
    }

    return `assets/audio/turn/${voiceLib}/`;
  }

  public setSpeechActive(speechActive: boolean): void {
    this.speechActive = speechActive;

    this.consoleLoggerService.log("speech active", this.speechActive);
  }

  // merge mp3 files into Uint8Array
  private MergeArrayBuffersFilesToUint8Arr(buffers: ArrayBuffer[]): Uint8Array {
    let byteArrays: Uint8Array[] = [];
    let length = 0;

    buffers.forEach(buffer => {
      let byteArray = new Uint8Array(buffer);
      let arr = new Uint8Array(byteArray.length);
      for (let i = 0; i < byteArray.length; i++) {
        arr[i] = byteArray[i];
      }

      byteArrays.push(arr);
      length += arr.length;
    });

    let mergedArray = new Uint8Array(length);
    let offset = 0;
    byteArrays.forEach(item => {
      mergedArray.set(item, offset);
      offset += item.length;
    });

    return mergedArray;
  }

  // convert mp3 file into Uint8Array
  private ArrayBufferToUint8Arr(buffer: ArrayBuffer): Uint8Array {
    let byteArray = new Uint8Array(buffer);
    let arr = new Uint8Array(byteArray.length);
    for (let i = 0; i < byteArray.length; i++) {
      arr[i] = byteArray[i];
    }

    return arr;
  }

  public RoundDistance(dist: number, unit: UnitSettings): number {
    if (unit.Distance == Unit.Metric) {
      return this.roundMetres(dist);
    }
    // Round in yards, not in metres: rounding metres first gives "109 yards" for 100 m.
    return this.roundYards(this.utilsService.mToYard(dist));
  }

  private roundMetres(dist: number): number {
    if (dist >= 0 && dist <= 20) {
      return Math.round(dist);
    }
    if (dist > 20 && dist <= 250) {
      return Math.round(dist / 10) * 10;
    }
    if (dist > 250 && dist <= 1000) {
      return Math.round(dist / 50) * 50;
    }
    if (dist > 1000 && dist <= 100000) {
      return Math.round(dist / 100) * 100;
    }
    // Negative, NaN and over 100 km: whole kilometres.
    return Math.round(dist / 1000) * 1000;
  }

  private roundYards(yards: number): number {
    if (yards <= 20) {
      return Math.round(yards);
    }
    if (yards <= 250) {
      return Math.round(yards / 10) * 10;
    }
    if (yards <= 850) {
      return Math.round(yards / 50) * 50;
    }
    if (yards < 1760) {
      return Math.round(yards / 100) * 100;
    }
    // A mile or more is read in miles; getSoundFiles rounds those to a tenth.
    return Math.round(yards);
  }
}
